/**
 * The one place the public site's origin is defined.
 *
 * Canonical tags, Open Graph URLs, JSON-LD, the sitemap and robots all build
 * absolute URLs from SITE_ORIGIN. It used to be assembled in ~20 places from
 * two different env vars (NEXT_PUBLIC_SITE_URL on some pages,
 * NEXT_PUBLIC_BASE_URL on others) plus hardcoded literals, so the host could
 * only be switched by editing every one of them — and they only agreed while
 * both vars held the same value.
 *
 * To serve canonicals from another host (e.g. www), set NEXT_PUBLIC_SITE_URL
 * at build time. NEXT_PUBLIC_BASE_URL is still honoured as a fallback so
 * existing deployments keep resolving exactly as they do today.
 *
 * next-sitemap.config.js is CommonJS and can't import this module; it reads
 * the same env vars in the same order.
 */

export const DEFAULT_SITE_ORIGIN = 'https://docupro.in';

/** Hosts that are this site, whichever one is canonical. */
const OWN_HOSTS = new Set(['docupro.in', 'www.docupro.in']);

/** Trims whitespace and trailing slashes; empty input gives the default. */
export function normalizeSiteOrigin(raw: string | undefined | null): string {
  const trimmed = (raw ?? '').trim().replace(/\/+$/, '');
  return trimmed || DEFAULT_SITE_ORIGIN;
}

// The env vars must be read literally: Next.js only inlines `NEXT_PUBLIC_*`
// into client bundles when accessed as `process.env.NAME`.
export const SITE_ORIGIN = normalizeSiteOrigin(
  process.env.NEXT_PUBLIC_SITE_URL || process.env.NEXT_PUBLIC_BASE_URL
);

/**
 * Re-bases an absolute URL on this site onto `origin` (SITE_ORIGIN by
 * default), keeping its path, query and hash. Used for canonicals supplied by
 * the backend, which may carry either host. URLs on other hosts, relative
 * URLs and empty values are returned unchanged.
 */
export function withSiteOrigin<T extends string | null | undefined>(
  url: T,
  origin: string = SITE_ORIGIN
): T | string {
  if (!url) return url;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  if (!OWN_HOSTS.has(parsed.hostname.toLowerCase())) return url;
  const rest = `${parsed.pathname}${parsed.search}${parsed.hash}`;
  return `${normalizeSiteOrigin(origin)}${rest === '/' ? '' : rest}`;
}
